import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ProgressBar } from 'primereact/progressbar';
import { checkVersion, forceRefresh } from '../serviceWorkerRegistration';

/**
 * Komponenta koja proverava i prikazuje obaveštenje o novoj verziji aplikacije
 */
const VersionChecker = () => {
    const [showDialog, setShowDialog] = useState(false);
    const toast = useRef(null);

    // Koristi useRef za currentBuild da bi bio dostupan u callback-ovima (closure fix)
    const currentBuildRef = useRef(null);
    // Flag da sprečimo višestruko prikazivanje toast-a
    const notificationShownRef = useRef(false);

    // Interval za proveru verzije (5 minuta)
    const CHECK_INTERVAL = 5 * 60 * 1000;

    useEffect(() => {
        // Učitaj trenutnu verziju pri mount-u
        loadCurrentVersion();

        // Proveri verziju pri mount-u (sa malim kašnjenjem)
        const initialCheck = setTimeout(() => {
            checkForUpdate();
        }, 5000);

        // Periodična provera
        const interval = setInterval(checkForUpdate, CHECK_INTERVAL);

        // Slušaj poruke od Service Worker-a
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', handleSWMessage);
        }

        return () => {
            clearTimeout(initialCheck);
            clearInterval(interval);
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.removeEventListener('message', handleSWMessage);
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadCurrentVersion = async () => {
        try {
            // Učitaj verziju iz lokalnog keša ili sa servera pri prvom učitavanju
            const data = await checkVersion();
            if (data) {
                const build = data.build || data.version;
                currentBuildRef.current = build;
                console.log('[Version] Trenutni build:', build);
            }
        } catch (error) {
            console.error('Greška pri učitavanju trenutne verzije:', error);
        }
    };

    const handleSWMessage = (event) => {
        if (event.data && event.data.type === 'VERSION_CHECK') {
            handleVersionResponse(event.data);
        }
    };

    const checkForUpdate = async () => {
        try {
            const response = await fetch(`${process.env.PUBLIC_URL || ''}/version.json?t=${Date.now()}`, {
                cache: 'no-store'
            });

            if (!response.ok) return;

            const data = await response.json();
            handleVersionResponse(data);
        } catch (error) {
            console.error('[Version Check] Greška:', error);
        }
    };

    const handleVersionResponse = (data) => {
        const serverBuild = data.build || data.version;

        console.log('[Version] Server build:', serverBuild, '| Trenutni build:', currentBuildRef.current);

        // Ako nemamo trenutni build, sačuvaj ga
        if (!currentBuildRef.current) {
            currentBuildRef.current = serverBuild;
            console.log('[Version] Sačuvan početni build:', serverBuild);
            return;
        }

        // Poredi build-ove
        if (serverBuild && serverBuild !== currentBuildRef.current && !notificationShownRef.current) {
            console.log(`[Version] Nova verzija detektovana: ${serverBuild} (trenutna: ${currentBuildRef.current})`);
            notificationShownRef.current = true;
            showUpdateNotification(data);
        }
    };

    const showUpdateNotification = (versionData) => {
        if (toast.current) {
            toast.current.show({
                severity: 'info',
                sticky: true,
                closable: false,
                content: (
                    <div className="flex flex-column align-items-start w-full">
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-cloud-download text-2xl text-blue-500"></i>
                            <span className="font-bold text-lg">Nova verzija dostupna!</span>
                        </div>
                        <div className="text-sm mt-2 mb-3">
                            Verzija <strong>{versionData.version}</strong>
                            {versionData.build && <span className="text-500"> (Build: {versionData.build})</span>}
                            <br />
                            Osvežite aplikaciju da biste dobili najnovije funkcionalnosti.
                        </div>
                        <div className="flex gap-2">
                            <Button label="Osveži sada" icon="pi pi-refresh" size="small" onClick={handleUpdate} />
                            <Button label="Kasnije" icon="pi pi-times" size="small" severity="secondary" outlined onClick={() => toast.current.clear()} />
                        </div>
                    </div>
                )
            });
        }
    };

    const handleUpdate = () => {
        setShowDialog(true);

        // Malo kašnjenje za UX
        setTimeout(() => {
            forceRefresh();
        }, 1500);
    };

    const dialogFooter = (
        <div className="flex justify-content-center">
            <ProgressBar mode="indeterminate" style={{ height: '6px', width: '100%' }} />
        </div>
    );

    return (
        <>
            <Toast ref={toast} position="bottom-right" />

            <Dialog visible={showDialog} onHide={() => {}} closable={false} header="Ažuriranje aplikacije" footer={dialogFooter} style={{ width: '400px' }}>
                <div className="flex flex-column align-items-center p-4">
                    <i className="pi pi-spin pi-spinner text-5xl text-primary mb-3"></i>
                    <p className="text-center m-0">
                        Učitavam novu verziju aplikacije...
                        <br />
                        <small className="text-500">Molimo sačekajte</small>
                    </p>
                </div>
            </Dialog>
        </>
    );
};

export default VersionChecker;
